/**
 * What an invoice carries in its adenda fields, given the form's three inputs.
 *
 * The form keeps "this invoice's edited copy of the adenda fija"
 * (`standingOverride`) SEPARATE from "this invoice's own note" (`adendaText`)
 * right up to submit, and this is where they become the two fields the backend
 * stores. There is no column for an override and none is needed: an override is
 * exactly `adendaSuppressDefault = true` plus the edited text, which is what
 * `resolveInvoiceAdendaFromSources` already composes.
 *
 * KEEPING THEM SEPARATE IS THE POINT. `adendaText` may already hold a note the
 * user typed; if taking an override wrote straight into that field it would
 * delete their text, and they would not find out until the CFE was issued.
 *
 * ORDER IS FISCAL, NOT COSMETIC. The standing text goes first because Res. DGI
 * 1264/023 numeral 3 requires mandatory legends "previo a cualquier mención no
 * obligatoria", and the standing text is where a tenant's required legend
 * lives. The backend resolver joins its own parts in the same order with the
 * same blank line, so an overridden invoice and a non-overridden one print
 * identically.
 *
 * A TWIN lives at apps/mobile/lib/adendaPayload.ts — mobile takes no dependency
 * on portal or shared-utils (see lib/businessDayOrdinal.ts for the convention).
 * Change both together; the tests live here.
 */
export interface AdendaPayload {
  adendaText: string;
  adendaSuppressDefault: boolean;
}

export function composeAdendaPayload(
  /** This invoice's edited copy of the adenda fija; null = use it unchanged. */
  standingOverride: string | null,
  /** This invoice's own note, appended below the standing text. */
  adendaText: string,
  /** The "no incluir la adenda fija" checkbox. */
  adendaSuppressDefault: boolean,
): AdendaPayload {
  // SUPPRESS WINS OVER AN OVERRIDE. An edited copy of the adenda fija is still
  // the adenda fija, so a user who ticks "no incluir la adenda fija" after
  // editing it must not get the edited text printed anyway — that is the
  // checkbox silently not working, on a fiscal document.
  if (standingOverride === null || adendaSuppressDefault) {
    return { adendaText, adendaSuppressDefault };
  }
  return {
    adendaText: [standingOverride, adendaText]
      .map((part) => part.trim())
      .filter((part) => part.length > 0)
      .join("\n\n"),
    // An override REPLACES the standing text for this invoice, so the stored
    // one must not also print — the override already contains everything the
    // user wants from it.
    adendaSuppressDefault: true,
  };
}
